import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Trophy, MapPin, Calendar, ShieldCheck, ArrowUpRight, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${BACKEND_URL}/api`;

const flag = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  const base = 0x1F1E6 - 65;
  return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => c.charCodeAt(0) + base));
};

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

export default function PartnerProfile() {
  const { alias } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/partner/${encodeURIComponent(alias)}`)
      .then((r) => setPartner(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [alias]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center text-sm text-slate-500">Loading partner profile…</div>;
  }
  if (notFound || !partner) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-center px-4">
        <div className="max-w-md">
          <div className="text-2xl font-semibold text-slate-900">Partner not found</div>
          <p className="mt-2 text-slate-600 text-sm">This partner alias doesn't exist or has been deactivated.</p>
          <Link to="/leaderboard"><Button className="mt-5 rounded-full bg-[#0055FF] hover:bg-[#0044CC]">See the leaderboard</Button></Link>
        </div>
      </div>
    );
  }

  // Sparkline path
  const spark = partner.spark_30d || [];
  const sparkW = 720, sparkH = 80, pad = 6;
  const max = Math.max(...spark, 1);
  const points = spark.map((v, i) => {
    const x = pad + (i / Math.max(1, spark.length - 1)) * (sparkW - pad * 2);
    const y = sparkH - pad - (v / max) * (sparkH - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-60 pointer-events-none" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-2 text-[#0055FF] mb-4">
            <Trophy className="size-4" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Connexis Partner</span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="text-5xl">{flag(partner.country)}</div>
              <div>
                <h1 className="text-4xl sm:text-5xl tracking-[-0.03em] font-semibold text-slate-900 flex items-center gap-3">
                  {partner.alias}
                  {partner.verified_partner && (
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wider bg-[#0055FF]/10 text-[#0055FF] px-2.5 py-1 rounded-full" data-testid="verified-badge">
                      <BadgeCheck className="size-3.5" /> Verified
                    </span>
                  )}
                </h1>
                <div className="mt-2 flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                  {partner.city && <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5" /> {partner.city}</span>}
                  {partner.joined_at && (
                    <span className="inline-flex items-center gap-1.5"><Calendar className="size-3.5" /> Joined {new Date(partner.joined_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</span>
                  )}
                  <span className="inline-flex items-center gap-1.5 text-[#0055FF] font-semibold capitalize"><ShieldCheck className="size-3.5" /> {partner.tier_label} tier</span>
                </div>
              </div>
            </div>
            <Link to="/partners/apply">
              <Button className="rounded-full h-11 px-5 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="partner-profile-apply-cta">
                Become a partner <ArrowUpRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* COMMISSION STAT + SPARKLINE */}
      <section className="py-14">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-[#0F172A] text-white p-7 sm:p-9 overflow-hidden relative" data-testid="partner-profile-stats">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] bg-[size:18px_18px] pointer-events-none" />
            <div className="relative grid sm:grid-cols-3 gap-7 sm:gap-8">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-mono">Commission · 30 days</div>
                <div className="mt-1.5 text-4xl sm:text-5xl font-semibold tabular-nums leading-none">{fmtGBP(partner.commission_30d)}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-mono">Lifetime</div>
                <div className="mt-1.5 text-4xl sm:text-5xl font-semibold tabular-nums leading-none text-[#22D3A6]">{fmtGBP(partner.lifetime_commission)}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-mono">Conversion · 30d</div>
                <div className="mt-1.5 text-4xl sm:text-5xl font-semibold tabular-nums leading-none">{partner.conversion_30d_pct}<span className="text-2xl text-white/50">%</span></div>
              </div>
            </div>

            {spark.length > 0 && (
              <div className="relative mt-7">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-2">Earnings curve · 30-day</div>
                <svg width="100%" height={sparkH} viewBox={`0 0 ${sparkW} ${sparkH}`} preserveAspectRatio="none" className="w-full">
                  <defs>
                    <linearGradient id="pp-spark-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0055FF" stopOpacity="0.45" />
                      <stop offset="100%" stopColor="#0055FF" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <polyline points={`${points} ${sparkW - pad},${sparkH - pad} ${pad},${sparkH - pad}`} fill="url(#pp-spark-fill)" stroke="none" />
                  <polyline points={points} fill="none" stroke="#3D7BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}

            <div className="relative mt-6 grid grid-cols-2 gap-3 pt-5 border-t border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">Signups · 30d</div>
                <div className="mt-1 text-lg font-semibold tabular-nums">{partner.signups_30d.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">Clicks · 30d</div>
                <div className="mt-1 text-lg font-semibold tabular-nums">{partner.clicks_30d.toLocaleString()}</div>
              </div>
            </div>
          </div>

          <p className="mt-6 text-xs text-slate-500 text-center">
            All figures are partner-attested + verified against on-chain Connexis ping-tree commissions.
            <br />
            Personal details are anonymised — never PII.
          </p>

          <div className="mt-10 text-center">
            <Link to="/leaderboard">
              <Button variant="outline" className="rounded-full" data-testid="partner-profile-back-leaderboard">
                ← All partners on the leaderboard
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
